import React, { createContext, useState, useEffect } from 'react';

import { IHttpRequest } from  '@/interfaces/IHttp';
import { container, TYPES } from "../inversifyconfig/container.config";
import { 
    DDOptions,
    LockerBatch,
    LockerBatchResponse,
    LockerContextRefreshType as refreshType, 
    LockerEvent, 
    LockerEventStatus, 
    SearchAndFilterParams
} from  '@/interfaces/LockerManagement';
import { getShortMonthFromEnum } from  '@/helpers/Date';


interface LockerManagmentContextProps {
    batches: LockerBatch[]
    archivedBatches: LockerBatch[]
    events: LockerEvent[]
    isLoading: boolean
    searchParams: SearchAndFilterMap
    totalFiles: number
    nextSkip: number
    currentTable: refreshType
    filterOptions: {events: DDOptions[], archive: DDOptions[], batches: DDOptions[]}
    refreshData: (type: refreshType) => void
    nextPage: (type: refreshType) => void
    searchAndFilter: (table: refreshType, params?: SearchAndFilterParams) => void
    setEvents: (events :LockerEvent []) => void
}

interface SearchAndFilterMap {
    events: SearchAndFilterParams
    archive: SearchAndFilterParams
    batches: SearchAndFilterParams
}

// Create context with placeholder functions - function definitions replaced in the provider
export const LockerManagementContext = createContext<LockerManagmentContextProps>({
    batches:[],
    archivedBatches: [],
    events: [],
    isLoading: false,
    searchParams: {events: {}, archive: {}, batches: {}},
    totalFiles: 0,
    nextSkip: 0,
    filterOptions: {events: [], archive: [], batches: []},
    currentTable: refreshType.ARCHIVE,
    refreshData: (type: refreshType) => {},
    nextPage: (type: refreshType) => {},
    searchAndFilter: (table: string, params?: SearchAndFilterParams) => {},
    setEvents: (events :LockerEvent []): void => {}
});

export const LockerManagementContextProvider = ({children}) => { 
    const httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);  
    const [batches, setBatches] = useState<LockerBatch[]>([]);
    const [archivedBatches, setArchivedBatches] = useState<LockerBatch[]>([]);
    const [events, setEvents] = useState<LockerEvent[]>([]);
    const [totalFiles, setTotalFiles] = useState<number>()
    const [currentTable, setCurrentTable] = useState<refreshType>(getDefaultTable());
    const [nextSkip, setNextSkip] = useState<number>(0);
    const [searchParams, setSearchParams] = useState<SearchAndFilterMap>({events: {}, archive: {}, batches: {}});
    const [filterOptions, setFilterOptions] = useState<any>({events: [], archive: [], batches: []});
    

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const refreshMap = {
        [refreshType.ARCHIVE]: {url: 'locker/batches/files/archived', setState: setArchivedBatches, state: archivedBatches},
        [refreshType.BATCHES]: {url:'locker/batches/files', setState: setBatches, state: batches},
        [refreshType.EVENTS]: {url: 'locker/batches/events', setState: setEvents, state: events}
    }

    // store param props as primitives, to trigger effect when any of them change
    const {searchTerm, filter, skip, sortBy, sort } = searchParams[currentTable]

    useEffect(() => {
        if (searchTerm || filter || skip || sortBy || sort) {
            refreshData(currentTable);
        }  
    }, [searchTerm, filter, skip, sortBy, sort, currentTable])

    function updateFilterOptions() {
        if (currentTable === refreshType.EVENTS) {
            setFilterOptions({...filterOptions,
                events: [
                {value: '', label: 'All Statuses'},
                {value: LockerEventStatus.COMPLETED, label: 'Completed'},
                {value: LockerEventStatus.WITHERRORS, label: 'Received With Errors'},
                {value: LockerEventStatus.FAILED, label: 'Failed'},
            ]})
        }
        else {
            const path = `locker/batches/options${currentTable === refreshType.ARCHIVE ? '/archived' : ''}`
            httpCall.get(path)
                .then(({data}) => {
                    setFilterOptions({...filterOptions, [currentTable]:formatSearchOptions(data)})
                })
        }
        
    }

    function getDefaultTable(): refreshType {
        const path = window.location.pathname.split('/')
    
        if (['LockerFiles', 'ArchivedFiles', 'EventOverview'].includes(path[path.length - 1])) {
            return {
                'LockerFiles': refreshType.BATCHES,
                'ArchivedFiles': refreshType.ARCHIVE,
                'EventOverview': refreshType.EVENTS
            }[path[path.length - 1]]
        }
        return refreshType.BATCHES
    }

   function refreshData(type: refreshType): void {
        setCurrentTable(type);
        setIsLoading(true);
        setTotalFiles(0);
        updateFilterOptions();
        const {url, setState } = refreshMap[type]
        
        httpCall.get(url, {params: {...getQueryParams(type)}})
        .then(({data}:{data: LockerBatchResponse}) => {
            setState(data.batches)
            setTotalFiles(data.totalfiles)
            setNextSkip(data.nextSkip)
        })
        .catch(() => {
           // error handling
        })
        .finally(() => setIsLoading(false))  
    }

    function formatSearchOptions(data):{value: any, label: string}[] {
        let options = []
        if(data.dateFilter) {
            for (const option of data.dateFilter) {
                const optionsForYear = option.months.map((month : number) => ({
                    value: {year: option.year, month},
                    label: `${getShortMonthFromEnum(month)} ${option.year}`
                }))
                options = [...options, ...optionsForYear]
            }
        }
            
        return options
    }

    function getQueryParams(table) {
        const queryParams = {}
        if (searchParams[table].searchTerm) {
            queryParams['searchTerm'] = searchParams[table].searchTerm
        }
        if (searchParams[table].filter || searchParams[table].filter === 0) {
            if (table === refreshType.EVENTS) {
                queryParams['statusToFilter'] = searchParams[table].filter
            }
            else {
                queryParams['dateFilter[0].Year'] = searchParams[table].filter.year
                queryParams['dateFilter[0].Months[0]'] = searchParams[table].filter.month
            }
            
        }
        if (searchParams[table].skip) {
            queryParams['paginationOptions'] = {
                Top: 20,
                Skip: searchParams[table].skip
            }
        }
        if (searchParams[table].sortBy && searchParams[table].sort) {
            const paramMap = {
                'Batch ID': 'BatchName',
                'Removed By': 'RemovedBy',
                Files: {[refreshType.EVENTS]: 'IntentFiles', [refreshType.ARCHIVE]: 'ArchivedFileCount', [refreshType.BATCHES]: 'NonArchivedFileCount'}[table],
                Users: {[refreshType.ARCHIVE]: 'ArchivedFileAffectedUserCount', [refreshType.BATCHES]: 'NonArchivedUserCount'}[table],
                'Users/Groups': 'IntentUsers'
            }
            
            if(searchParams[table].sortBy.toLowerCase().includes('date')) {
                queryParams['sortBy'] =  "LastUpdate"
            }

            else if (paramMap[searchParams[table].sortBy]) {
                queryParams['sortBy'] = paramMap[searchParams[table].sortBy]
            }
            
            queryParams['sort'] = searchParams[table].sort
        }
        return queryParams
    }

    function nextPage(table:refreshType): void {
        setIsLoading(true)
        const {url, setState, stub, state } = refreshMap[table]
        httpCall.get(url, {params: {...getQueryParams(table), Skip: nextSkip}})
            .then(({data}) => {
                setState([...state, ...data.batches]);
                setTotalFiles(data.totalFiles);
                setNextSkip(data.nextSkip);
            })
            .catch(() => {
                setState([...stub.batches, ...state]);
                setTotalFiles(stub.totalFiles);
                setNextSkip(stub.nextSkip);
            })
            .finally(() => setIsLoading(false))
        

    }

    function searchAndFilter(table: refreshType, params: SearchAndFilterParams = {}) {
        
        setCurrentTable(table);
        setSearchParams({...searchParams, [table]: {...searchParams[table], ...params}});
    }

    const lockerContextValues = {
        batches,
        archivedBatches,
        events,
        isLoading,
        searchParams,
        filterOptions,
        totalFiles,
        nextSkip,
        currentTable,
        refreshData,
        searchAndFilter,
        nextPage,
        setEvents
    }

    return (
        <LockerManagementContext.Provider value={lockerContextValues}>
            {children}
        </LockerManagementContext.Provider>
    )
}